import React, { useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import Icon from '../Brand/Icon';
import config from '../../appLinks/config.json';
import { contentUrl, installUrl, openAppUrl, smartBannerContent } from '../../appLinks/links';
import { browserPlatform, isSafari } from '../../appLinks/platform';
import labels from '../../appLinks/translations';
import { appSessionStorage } from '../../utils/storage';
import './index.scss';

const dismissalKey = 'app-install-banner-dismissed';
const dismissedPreviously = () => {
  try { return appSessionStorage.getItem(dismissalKey) === '1'; } catch { return false; }
};

export default function AppInstallBanner({ suppressed = false }) {
  const { pathname, search } = useLocation();
  const { i18n } = useTranslation('brand', { useSuspense: false });
  const [dismissed, setDismissed] = useState(dismissedPreviously);
  const platform = browserPlatform();
  const banner = useRef(null);
  const meta = !suppressed && platform === 'ios' ? smartBannerContent(pathname, search) : null;
  const visible = !suppressed && config.enabled && platform && contentUrl(pathname) && !dismissed
    && !(meta && isSafari());
  const [open, download, close] = labels[(i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]] || labels.en;
  const store = installUrl(platform);

  useLayoutEffect(() => {
    if (!visible || !banner.current) return;
    const root = document.documentElement;
    const update = () => root.style.setProperty('--app-banner-height', `${banner.current?.getBoundingClientRect().height || 0}px`);
    update();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    observer?.observe(banner.current);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
      root.style.removeProperty('--app-banner-height');
    };
  }, [visible]);

  const dismiss = () => {
    setDismissed(true);
    try { appSessionStorage.setItem(dismissalKey, '1'); } catch { /* Storage can be unavailable in private browsing. */ }
  };

  return <>
    {meta && <Helmet><meta name="apple-itunes-app" content={meta} /></Helmet>}
    {visible && <aside ref={banner} className="app-install-banner" aria-label="WIW">
      <img src="/logo192.png" alt="" width="32" height="32" />
      <div className="app-install-banner__actions">
        <a className="app-install-banner__open" href={openAppUrl(platform, pathname, search)}>{open}</a>
        {store && <a href={store} rel="noreferrer">{download}</a>}
      </div>
      <button type="button" onClick={dismiss} aria-label={close} title={close}><Icon name="close" size={20} /></button>
    </aside>}
  </>;
}
